"use client";

import * as React from "react";

/**
 * Where app-level dialogs render, so a sheet opened from inside another sheet
 * STACKS on it (the parent recedes behind the child, Base UI's nested
 * drawer). Nesting is a React-tree fact — a dialog mounted once at the app
 * root is never inside the sheet that opened it — so the provider holds the
 * app's dialog mounts as a render function and hands it to the innermost open
 * host (`DialogHost`, which every sheet carries), falling back to the root
 * while no host is open.
 *
 * Dialogs rendered through a host never register as hosts themselves: a host
 * inside the hosted mounts would re-render the mounts inside themselves.
 */
type DialogHostValue = {
  register: (id: string) => () => void;
  topId: string | null;
  render: () => React.ReactNode;
};

const DialogHostContext = React.createContext<DialogHostValue | null>(null);
const HostedContext = React.createContext(false);

export function DialogHostProvider({
  render,
  children,
}: {
  /** The app-level dialog mounts — rendered once, wherever the innermost
   *  open host is. */
  render: () => React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  const [stack, setStack] = React.useState<string[]>([]);
  const register = React.useCallback((id: string) => {
    setStack((prev) => [...prev, id]);
    return () => setStack((prev) => prev.filter((entry) => entry !== id));
  }, []);
  const topId = stack.at(-1) ?? null;
  const value = React.useMemo(() => ({ register, topId, render }), [register, topId, render]);
  return (
    <DialogHostContext.Provider value={value}>
      {children}
      {topId === null ? (
        <HostedContext.Provider value>{render()}</HostedContext.Provider>
      ) : null}
    </DialogHostContext.Provider>
  );
}

/**
 * The outlet a sheet carries: while it is the innermost open host, the app's
 * dialog mounts render here, inside the sheet's tree. Responsive sheets
 * (`DialogPopup`) include one; a hand-built Drawer sheet places its own.
 */
export function DialogHost(): React.ReactElement | null {
  const ctx = React.useContext(DialogHostContext);
  const hosted = React.useContext(HostedContext);
  const id = React.useId();
  const register = ctx?.register;
  React.useLayoutEffect(() => {
    if (!register || hosted) return;
    return register(id);
  }, [register, hosted, id]);
  if (!ctx || hosted || ctx.topId !== id) return null;
  return <HostedContext.Provider value>{ctx.render()}</HostedContext.Provider>;
}
